import { useEffect, useState, useCallback } from 'react';
import { useThemeContext } from '../../../../features/Theme/providers/ThemeContextProvider';
import {
	useTranslationsContext
} from '../../../../features/Translations/providers/TranslationsContextProvider';
import SessionIcon from '../../../assets/icons/session.svg?react';
import { Column } from '../../../components/Column/Column';
import { Button } from '../../../components/Button/Button';
import { Modal } from '../../../components/Modal/Modal';

export interface DevicesInUseModalProps {
	show?: boolean;
	onClickBtn?: () => void;
}

const RETRY_COUNTDOWN_SECONDS = 10;

export function DevicesInUseModal({show = false, onClickBtn}: Readonly<DevicesInUseModalProps>) {
	const {t} = useTranslationsContext();
	const theme = useThemeContext();

	const [countdown, setCountdown] = useState(RETRY_COUNTDOWN_SECONDS);
	const [isDisabled, setIsDisabled] = useState(true);

	const startCountdown = useCallback(() => {
		setCountdown(RETRY_COUNTDOWN_SECONDS);
		setIsDisabled(true);
	}, []);

	useEffect(() => {
		if (!isDisabled) return;

		if (countdown > 0) {
			const timerId = setTimeout(() => setCountdown((prev) => prev - 1), 1000);
			return () => clearTimeout(timerId);
		}

		setIsDisabled(false);
	}, [countdown, isDisabled]);

	const handleClose = useCallback(() => {
		onClickBtn?.();
	}, [onClickBtn]);

	const handleRetry = useCallback(() => {
		startCountdown();
		handleClose();
	}, [startCountdown, handleClose]);

	const countdownText = isDisabled ? ` (${countdown}s)` : '';

	return (
		<Modal
			data-testid="devices-in-use-modal"
			show={show}
			closeDisabled
			title={t('errors.devices_in_use_title')}
			$fitToChildren
			onClose={handleClose}
			prepend={
				<SessionIcon
					fill={theme.colors.brand.primary[300]}
					width={25}
					height={25}
				/>
			}
		>
			<Column $spaceBetween $fillHeight $gap={32}>
				<p>{t('errors.devices_in_use_description')}</p>
				<Button
					onClick={handleRetry}
					$color="primary"
					$block
					$rounded
					disabled={isDisabled}
				>
					{`${t('common.retry')}${countdownText}`}
				</Button>
			</Column>
		</Modal>
	);
}
